// src/router/index.ts
import type { Router, RouterOptions, RouteRecordRaw } from 'vue-router'

import { useLocaleStore } from '@/stores/locale.store'
import { routeTranslations } from './routes'
import { routeDefs } from './routeDefs'

import { DEFAULT_LOCALE, PREFIX_LOCALES, type Locale } from './locales'
import { normalizeLocale } from '@/utils/locale'

// --- helpers

function isPrefixLocale(value: string): value is Exclude<Locale, typeof DEFAULT_LOCALE> {
  return (PREFIX_LOCALES as readonly string[]).includes(value)
}

function childrenFor(lang: Locale): RouteRecordRaw[] {
  return routeDefs.map((def) => {
    const path = routeTranslations[def.key][lang] ?? ''

    const name =
      def.nameBase
        ? (lang === DEFAULT_LOCALE ? def.nameBase : `${def.nameBase}-${lang}`)
        : undefined

    const record: RouteRecordRaw = {
      path,
      component: def.component,
      ...(def.meta ? { meta: def.meta } : {}),
      ...(name ? { name } : {})
    }

    return record
  })
}


// --- routes-

export const routes: RouteRecordRaw[] = [
  // Default locale at the root (without /no)
  {
    path: '/',
    children: childrenFor(DEFAULT_LOCALE)
  },

  // Only non-default locales with a prefix (/en, /sv, ...)
  ...PREFIX_LOCALES.map((lang) => ({
    path: `/${lang}`,
    children: childrenFor(lang)
  })),

  // 404 -> root (default locale)
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const firstSeg = normalizeLocale(to.path.split('/')[1] ?? '')
      const lang: Locale = isPrefixLocale(firstSeg) ? firstSeg : DEFAULT_LOCALE
      const notFoundPath = routeTranslations.notFound[lang]

      return lang === DEFAULT_LOCALE ? `/${notFoundPath}` : `/${lang}/${notFoundPath}`
    }
  }
]

export const routerOptions: Omit<RouterOptions, 'history'> = {
  routes,
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
}

/**
 * Keeps the locale store aligned with the active route in both the browser and
 * Vite SSG's memory router.
 */
export function installRouteLocaleSync(router: Router): void {
  router.afterEach((to) => {
    const store = useLocaleStore()

    const firstSeg = normalizeLocale(to.path.split('/')[1] ?? '')

    const lang: Locale = isPrefixLocale(firstSeg) ? firstSeg : DEFAULT_LOCALE

    store.setLocale(lang)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('locale', lang)
    }
  })
}
